import {
    ArchiveBoxIcon,
    ArrowTopRightOnSquareIcon,
    PencilSquareIcon,
    RectangleStackIcon,
    ShareIcon,
    TrashIcon,
} from "@heroicons/react/20/solid";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import {
    Alert,
    AlertActions,
    AlertDescription,
    AlertTitle,
    Button,
    EmptyState,
    Heading,
    Text,
    notify,
} from "components";
import { PrimaryButton } from "src/components/primary-button";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "src/api/api";
import type { CollectionOverviewResponse } from "src/api/generated";
import { CollectionDialog } from "src/components/collection-dialog";
import { CollectionTile } from "src/components/collection-tile";
import { ContextMenu, useContextMenu } from "src/components/context-menu";
import type { ContextMenuSection } from "src/components/context-menu";
import { RequireAccount } from "src/components/require-account";
import { ShareDialog } from "src/components/share-dialog";
import { formatCurrency } from "src/utils/format";
import { useShortcuts } from "src/utils/use-shortcuts";
import { useShortcutHelpOpen } from "src/context/shortcut-help-context";

export const Route = createFileRoute("/_menu/collections/")({
    // In the loader, so hovering the navbar entry already fetches the list.
    loader: () => Api.collections.list(),
    component: RouteComponent,
});

/**
 * The account's collections, each one a physical container: a crate, a binder, a shelf.
 *
 * @returns the page
 */
function RouteComponent() {
    const [t] = useTranslation("collection");
    const [tg] = useTranslation();
    const collections = Route.useLoaderData();
    const router = useRouter();
    const navigate = useNavigate();
    const [dialog, setDialog] = useState<{ collection: CollectionOverviewResponse | null } | null>(null);
    const [sharing, setSharing] = useState<CollectionOverviewResponse | null>(null);
    const [confirming, setConfirming] = useState<CollectionOverviewResponse | null>(null);
    const [selected, setSelected] = useState<string | null>(null);
    const menu = useContextMenu<CollectionOverviewResponse>();
    const shortcutHelpOpen = useShortcutHelpOpen();

    // A deck's own collection is not one of the shelved ones: it stands for
    // cards that are sleeved up right now. It still counts towards the totals,
    // because those cards are as owned as any other.
    const shelf = collections.filter((overview) => overview.collection.deck == null);
    const inDecks = collections.filter((overview) => overview.collection.deck != null);
    const cards = collections.reduce((total, overview) => total + overview.cards, 0);
    const value = collections.reduce((total, overview) => total + overview.price_eur_cents, 0);

    const marked = collections.find((overview) => overview.collection.uuid === selected) ?? null;
    const editable = marked !== null && marked.collection.deck == null ? marked : null;

    useShortcuts(
        {
            a: () => setDialog({ collection: null }),
            enter: () => {
                if (marked !== null) {
                    void navigate({
                        to: "/collections/$collectionUuid/cards",
                        params: { collectionUuid: marked.collection.uuid },
                    });
                }
            },
            e: () => {
                if (editable !== null) setDialog({ collection: editable });
            },
            s: () => {
                if (editable !== null) setSharing(editable);
            },
            delete: () => {
                if (editable !== null) setConfirming(editable);
            },
        },
        dialog === null && sharing === null && confirming === null && menu.open === null && !shortcutHelpOpen,
    );

    /**
     * Re-runs the loader after a write, so the list on screen matches the server
     *
     * @returns a promise resolving once the loader has finished
     */
    const refresh = () => router.invalidate();

    /**
     * Deletes a collection after the confirmation was accepted
     *
     * @param overview the collection to delete
     */
    async function remove(overview: CollectionOverviewResponse) {
        setConfirming(null);
        await Api.collections.delete(overview.collection.uuid);
        notify.success(t("toast.collection-deleted"));
        await refresh();
    }

    /**
     * What one collection can be told from its menu
     *
     * @param overview the collection the menu was opened on
     *
     * @returns the lines, grouped the way the deck menu groups its own
     */
    function sectionsFor(overview: CollectionOverviewResponse): Array<ContextMenuSection> {
        const open = {
            key: "open",
            label: t("button.open-collection"),
            icon: <ArrowTopRightOnSquareIcon />,
            onSelect: () =>
                void navigate({
                    to: "/collections/$collectionUuid/cards",
                    params: { collectionUuid: overview.collection.uuid },
                }),
        };

        const deckUuid = overview.collection.deck;
        if (deckUuid !== null && deckUuid !== undefined) {
            return [
                {
                    key: "collection",
                    items: [
                        open,
                        {
                            key: "deck",
                            label: t("button.open-deck"),
                            icon: <RectangleStackIcon />,
                            onSelect: () => void navigate({ to: "/decks/$deckUuid/cards", params: { deckUuid } }),
                        },
                    ],
                },
            ];
        }

        return [
            {
                key: "collection",
                items: [
                    open,
                    {
                        key: "share",
                        label: tg("button.share-ellipsis"),
                        icon: <ShareIcon />,
                        onSelect: () => setSharing(overview),
                    },
                    {
                        key: "edit",
                        label: t("button.edit-collection"),
                        icon: <PencilSquareIcon />,
                        onSelect: () => setDialog({ collection: overview }),
                    },
                ],
            },
            {
                key: "delete",
                items: [
                    {
                        key: "delete",
                        label: t("button.delete-collection"),
                        icon: <TrashIcon />,
                        tone: "danger",
                        onSelect: () => setConfirming(overview),
                    },
                ],
            },
        ];
    }

    return (
        <RequireAccount>
            <div className={"flex flex-col gap-6"}>
                <div className={"flex flex-wrap items-start justify-between gap-3"}>
                    <div className={"flex flex-col gap-2"}>
                        <Heading>{t("heading.collections")}</Heading>
                        {collections.length === 0 ? (
                            <Text>{t("description.collections")}</Text>
                        ) : (
                            <Text className={"flex flex-wrap items-center gap-x-2 gap-y-1"}>
                                <span>{t("label.collection-count", { count: collections.length })}</span>
                                <span aria-hidden={true}>·</span>
                                <span className={"tabular-nums"}>{t("label.card-count", { cards })}</span>
                                {value > 0 && (
                                    <>
                                        <span aria-hidden={true}>·</span>
                                        <span className={"tabular-nums"}>{formatCurrency(value / 100)}</span>
                                    </>
                                )}
                            </Text>
                        )}
                    </div>
                    <PrimaryButton onClick={() => setDialog({ collection: null })}>
                        {t("button.create-collection")}
                    </PrimaryButton>
                </div>

                {collections.length === 0 ? (
                    <EmptyState
                        icon={<ArchiveBoxIcon />}
                        title={t("heading.no-collections")}
                        description={t("description.no-collections")}
                    />
                ) : (
                    <>
                        <ul className={"grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"}>
                            {shelf.map((overview) => (
                                <CollectionTile
                                    key={overview.collection.uuid}
                                    overview={overview}
                                    onMenu={menu.openAt}
                                    selected={selected === overview.collection.uuid}
                                    onActivate={() => setSelected(overview.collection.uuid)}
                                />
                            ))}
                        </ul>

                        {inDecks.length > 0 && (
                            <section className={"flex flex-col gap-3"}>
                                <div className={"flex items-center gap-3"}>
                                    <h2 className={"text-sm/6 font-semibold text-zinc-950 dark:text-white"}>
                                        {t("heading.deck-collections")}
                                    </h2>
                                    <span
                                        className={
                                            "rounded-(--radius-pill) bg-zinc-950/5 px-2 py-0.5 text-xs font-medium text-zinc-600 tabular-nums dark:bg-white/10 dark:text-zinc-300"
                                        }
                                    >
                                        {inDecks.length}
                                    </span>
                                    <span className={"h-px flex-1 bg-zinc-950/5 dark:bg-white/10"} />
                                </div>

                                <ul className={"grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4"}>
                                    {inDecks.map((overview) => (
                                        <CollectionTile
                                            key={overview.collection.uuid}
                                            overview={overview}
                                            onMenu={menu.openAt}
                                            selected={selected === overview.collection.uuid}
                                            onActivate={() => setSelected(overview.collection.uuid)}
                                        />
                                    ))}
                                </ul>
                            </section>
                        )}
                    </>
                )}

                <ContextMenu
                    title={menu.open?.item.collection.name}
                    at={menu.open?.at ?? null}
                    sections={menu.open === null ? [] : sectionsFor(menu.open.item)}
                    onClose={menu.close}
                />

                <CollectionDialog
                    open={dialog !== null}
                    collection={dialog?.collection?.collection ?? null}
                    onClose={() => setDialog(null)}
                    onSaved={(created) => {
                        setDialog(null);
                        notify.success(
                            created !== null ? t("toast.collection-created") : t("toast.collection-updated"),
                        );
                        // A new collection is made to be filled, so that is where
                        // this ends up. The list behind it reloads on its
                        // own the next time it is looked at.
                        if (created !== null) {
                            void navigate({
                                to: "/collections/$collectionUuid/cards",
                                params: { collectionUuid: created.uuid },
                            });
                            return;
                        }
                        void refresh();
                    }}
                />

                <ShareDialog
                    subject={
                        sharing === null
                            ? null
                            : {
                                  kind: "collections",
                                  uuid: sharing.collection.uuid,
                                  visibility: sharing.collection.visibility,
                                  shareToken: sharing.collection.share_token ?? null,
                                  owner: true,
                              }
                    }
                    linkDescription={t("description.share-link")}
                    onClose={() => setSharing(null)}
                    onChanged={refresh}
                />

                <Alert open={confirming !== null} onClose={() => setConfirming(null)}>
                    <AlertTitle>{t("heading.delete-collection")}</AlertTitle>
                    <AlertDescription>
                        {t("description.delete-collection", { name: confirming?.collection.name ?? "" })}
                    </AlertDescription>
                    <AlertActions>
                        <Button plain onClick={() => setConfirming(null)}>
                            {tg("button.cancel")}
                        </Button>
                        <Button color={"red"} onClick={() => void (confirming && remove(confirming))}>
                            {t("button.delete-collection")}
                        </Button>
                    </AlertActions>
                </Alert>
            </div>
        </RequireAccount>
    );
}
